import React, { useState, useEffect, useRef, FunctionComponent, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFetch, usePost, notify } from '../../../components/component-items/helpers'
import { globalState } from '../../../store'
import styled from 'styled-components'
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns'

// Components
import GlobalStyles from '../../../components/component-items/styles'
import { TableCell } from '../../../components/component-items/datatable'
import { Form, Col, InputGroup, Row } from 'react-bootstrap'
import Checkbox from '../../../components/component-items/checkbox'
import { StatusBoxStyled } from '../../../components/component-items/status-box'
import GenericModal from '../../../components/component-items/modal'
import AsyncSelect from 'react-select/async'
import CreatableSelect from 'react-select/creatable'
import { HoverNotes } from '../../../components/component-items/hover-notes'
import { getOrdinal } from '../../../components/component-items/utils'
import { FullTabMenuCard } from '../../../components/component-items/tab-menu'
import { arrayMove } from '../../../components/component-items/sortable'
import { LargeButton } from '../../../components/component-items/buttons'
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { verticalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Tooltip } from './components/styles'
import { WaveOperationsSummary } from './components/wave-operations-summary'

// Cards
import { WaveCoverageModal, WaveProfile } from './profiles'
import { WavePreview } from './profiles/wave-preview'
import { WaveRunHistoryTable } from './run-history'
import { WaveScheduleDashboard, type WaveSchedule } from './schedules'
import { normalizeInventorySources, normalizeZoneValues } from './components/wave-utils'
import { buildWaveScheduleListUrl } from './schedules/schedule-utils'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTrashAlt,
  faLayerPlus,
  faHistory,
  faLayerGroup,
  faGripLines,
  faCalendarDay,
} from '@fortawesome/pro-duotone-svg-icons'

export type FiltersProps = {
  id: number
  type: string
  result: {
    order_field_type: string
    order_field_operator: string
    order_field_value: string
  }
  logical: (FiltersProps | any)[]
}

const defaultFilters: FiltersProps = {
  id: 1,
  type: '',
  result: {
    order_field_type: '',
    order_field_operator: '',
    order_field_value: '',
  },
  logical: [{}, {}],
}

export type SortProps = {
  id: string
  field_type: string
  field_value: string
  sort: string
}
const defaultSort: SortProps = {
  id: '1',
  field_type: 'order_field',
  field_value: '',
  sort: 'ascending',
}

type ReservationStrategy = {
  inventory_sources: {
    allowed: string[]
    ordering: string[]
  }
  location_attributes: LocationAttributes
  controlled_date_ordering: string
  pick_source_ordering: string
  lot_mixing: string
  container_ordering: string
  ordering_order: string[]
}

type LocationAttributePolicy = {
  include: string[]
  exclude: string[]
  ordering: string[]
}

type LocationAttributes = {
  zone: LocationAttributePolicy
  aisle: LocationAttributePolicy
  bay: LocationAttributePolicy
  level: LocationAttributePolicy
  slot: LocationAttributePolicy
}

type ReservationStep = ReservationStrategy & {
  id: string
  name: string
  shortage_behavior: string
  minimum_days_remaining?: string
  location_attributes: LocationAttributes
}

const defaultLocationAttributes: LocationAttributes = {
  zone: { include: [], exclude: [], ordering: [] },
  aisle: { include: [], exclude: [], ordering: [] },
  bay: { include: [], exclude: [], ordering: [] },
  level: { include: [], exclude: [], ordering: [] },
  slot: { include: [], exclude: [], ordering: [] },
}

const defaultReservationStrategy: ReservationStrategy = {
  inventory_sources: {
    allowed: ['pickable', 'backstock'],
    ordering: ['pickable', 'backstock'],
  },
  location_attributes: defaultLocationAttributes,
  controlled_date_ordering: 'product_default',
  pick_source_ordering: 'product_default',
  lot_mixing: 'allow',
  container_ordering: 'loose_or_container',
  ordering_order: [
    'controlled_date_ordering',
    'inventory_type_ordering',
    'location_ordering',
    'pick_source_ordering',
    'container_ordering',
  ],
}

const defaultReservationStep: ReservationStep = {
  ...defaultReservationStrategy,
  id: 'step-1',
  name: 'Primary reservation step',
  shortage_behavior: 'continue_to_next_step',
  minimum_days_remaining: '',
  location_attributes: defaultLocationAttributes,
}

const inventoryTypeOptions = [
  { value: 'pickable', label: 'Pickable' },
  { value: 'backstock', label: 'Backstock' },
  { value: 'defective', label: 'Defective' },
]

const orderingOrderOptions = [
  {
    value: 'controlled_date_ordering',
    label: 'Controlled Date Ordering',
    description: 'Order inventory by product control date or a directional override.',
  },
  {
    value: 'inventory_type_ordering',
    label: 'Inventory Type Ordering',
    description: 'Prefer inventory types in the selected ordering.',
  },
  {
    value: 'location_ordering',
    label: 'Location Ordering',
    description: 'Prefer eligible zones, aisles, bays, levels, and slots in the selected ordering.',
  },
  {
    value: 'pick_source_ordering',
    label: 'Pick Source Ordering',
    description: 'Apply product default, pick path, depletion, or quantity ordering.',
  },
  {
    value: 'container_ordering',
    label: 'Container Ordering',
    description: 'Apply loose or full-container ordering when configured.',
  },
]

const normalizeOrderingOrder = (orderingOrder: any) => {
  const validValues = orderingOrderOptions.map(({ value }) => value)
  const nextOrder = Array.isArray(orderingOrder) ? orderingOrder.filter((value) => validValues.includes(value)) : []
  const uniqueOrder = nextOrder.filter((value, index) => nextOrder.indexOf(value) === index)
  return uniqueOrder.concat(validValues.filter((value) => !uniqueOrder.includes(value)))
}

type ValueOption = { value: string; label: string }

const valuesToOptions = (values: string[] = []) => values.map((value) => ({ value, label: value }))

const textToStringList = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const addUniqueValue = (values: string[], nextValue: string) => {
  const value = nextValue.trim()
  if (!value || values.includes(value)) return values
  return values.concat(value)
}

type LocationValueSelectProps = {
  values: string[]
  options?: ValueOption[]
  onChange: (values: string[]) => void
  placeholder?: string
}

const LocationValueSelect: FunctionComponent<LocationValueSelectProps> = ({
  values,
  options = [],
  onChange,
  placeholder = 'Type value and press Enter',
}) => {
  const [inputValue, setInputValue] = useState('')

  const commitInputValue = () => {
    if (!inputValue.trim()) return
    onChange(addUniqueValue(values, inputValue))
    setInputValue('')
  }

  return (
    <CreatableSelect
      options={options}
      value={valuesToOptions(values)}
      inputValue={inputValue}
      onInputChange={(value, action) => {
        if (action.action === 'input-change') {
          setInputValue(value)
        }
      }}
      onChange={(selected: any) => {
        onChange((selected || []).map(({ value }: ValueOption) => value))
        setInputValue('')
      }}
      onCreateOption={(value) => {
        onChange(addUniqueValue(values, value))
        setInputValue('')
      }}
      onBlur={commitInputValue}
      onKeyDown={(event) => {
        if ([',', 'Enter', 'Tab'].includes(event.key) && inputValue.trim()) {
          event.preventDefault()
          commitInputValue()
        }
      }}
      closeMenuOnSelect={false}
      isClearable
      isMulti
      placeholder={placeholder}
    />
  )
}

const normalizeLocationAttributePolicy = (policy: any): LocationAttributePolicy => {
  if (typeof policy === 'string') {
    return { include: textToStringList(policy), exclude: [], ordering: [] }
  }
  return {
    include: Array.isArray(policy?.include) ? policy.include : [],
    exclude: Array.isArray(policy?.exclude) ? policy.exclude : [],
    ordering: Array.isArray(policy?.ordering) ? policy.ordering : [],
  }
}

const normalizeReservationStep = (step: any, index: number): ReservationStep => ({
  ...defaultReservationStep,
  ...step,
  id: step?.id || `step-${index + 1}`,
  name: step?.name || (index === 0 ? 'Primary reservation step' : `Fallback step ${index}`),
  inventory_sources: {
    ...defaultReservationStrategy.inventory_sources,
    ...(step?.inventory_sources || {}),
  },
  location_attributes: {
    zone: normalizeLocationAttributePolicy(step?.location_attributes?.zone),
    aisle: normalizeLocationAttributePolicy(step?.location_attributes?.aisle),
    bay: normalizeLocationAttributePolicy(step?.location_attributes?.bay),
    level: normalizeLocationAttributePolicy(step?.location_attributes?.level),
    slot: normalizeLocationAttributePolicy(step?.location_attributes?.slot),
  },
})

const normalizeReservationSteps = (settings: any, reservationStrategy: ReservationStrategy) => {
  const steps = settings.reservation_profile?.steps
  if (Array.isArray(steps) && steps.length) {
    return steps.map(normalizeReservationStep)
  }
  return [normalizeReservationStep({ ...reservationStrategy, name: 'Primary reservation step' }, 0)]
}

const formatListSummary = (values: string[], fallback: string) => {
  if (!values.length) return fallback
  if (values.length <= 2) return values.join(', ')
  return `${values.slice(0, 2).join(', ')} +${values.length - 2}`
}

const getStepLocationScope = (step: ReservationStep) => {
  const zones = normalizeZoneValues(step.location_attributes?.zone?.include || [])
  if (zones.length) return `Zones: ${formatListSummary(zones, '')}`

  const locationFields = (['zone', 'aisle', 'bay', 'level', 'slot'] as (keyof LocationAttributes)[]).filter((field) => {
    const policy = step.location_attributes?.[field]
    return Boolean(policy?.include?.length || policy?.exclude?.length || policy?.ordering?.length)
  })
  if (locationFields.length) return `${formatListSummary(locationFields, '')} rules`
  return 'Any eligible location'
}

const getDateStrategyLabel = (strategy: string) => {
  if (strategy === 'fefo') return 'Oldest controlled date'
  if (strategy === 'newest_expiry_last') return 'Newest controlled date'
  if (strategy === 'fifo') return 'Oldest received'
  if (strategy === 'lifo') return 'Newest received'
  return 'Product control'
}

const getLocationStrategyLabel = (strategy: string) => {
  if (strategy === 'deplete_location') return 'Fewest locations'
  if (strategy === 'largest_available_first') return 'Largest available first'
  if (strategy === 'smallest_available_first') return 'Smallest available first'
  if (strategy === 'pick_path') return 'Pick path'
  return 'Product location rule'
}

const SortableOrderingRule = ({ id, index }: { id: string; index: number }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })
  const option = orderingOrderOptions.find(({ value }) => value === id)
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <div className="ordering-row" ref={setNodeRef} style={style}>
      <button
        type="button"
        className="ordering-handle"
        ref={setActivatorNodeRef}
        {...listeners}
        {...attributes}
        aria-label={`Move ${option?.label}`}
      >
        <FontAwesomeIcon icon={faGripLines} />
      </button>
      <span className="ordering-index">{index + 1}</span>
      <div className="ordering-copy">
        <strong>{option?.label}</strong>
        <span>{option?.description}</span>
      </div>
    </div>
  )
}

type SortableReservationStepRowProps = {
  step: ReservationStep
  index: number
  active: boolean
  canRemove: boolean
  setActiveStepIndex: (index: number) => void
  updateStepName: (index: number, name: string) => void
  duplicateStep: (index: number) => void
  removeStep: (index: number) => void
}

const SortableReservationStepRow: FunctionComponent<SortableReservationStepRowProps> = ({
  step,
  index,
  active,
  canRemove,
  setActiveStepIndex,
  updateStepName,
  duplicateStep,
  removeStep,
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }

  return (
    <tr
      className={`step-row ${active ? 'active' : ''}`}
      onClick={() => setActiveStepIndex(index)}
      ref={setNodeRef}
      style={style}
    >
      <td className="step-drag-cell">
        <button
          type="button"
          className="step-drag-handle"
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Move step ${index + 1}`}
        >
          <FontAwesomeIcon icon={faGripLines} />
        </button>
      </td>
      <td>
        <span className="step-table-number">{index + 1}</span>
      </td>
      <td>
        <Form.Control
          className="step-table-name-input"
          value={step.name}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => updateStepName(index, event.target.value)}
        />
      </td>
      <td>{step.inventory_sources.allowed.join(', ')}</td>
      <td>{getStepLocationScope(step)}</td>
      <td className="step-method-cell">
        <span>{getDateStrategyLabel(step.controlled_date_ordering)}</span>
        <span>{getLocationStrategyLabel(step.pick_source_ordering)}</span>
      </td>
      <td className="step-action-cell">
        <div className="step-row-actions dropdown__container" data-no-dnd="true">
          <Tooltip className="subheader__button button" data-title="Duplicate Step">
            <LargeButton
              type="button"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                duplicateStep(index)
              }}
              aria-label={`Duplicate step ${index + 1}`}
            >
              <FontAwesomeIcon icon={faLayerPlus} />
            </LargeButton>
          </Tooltip>
          <Tooltip className="subheader__button button" data-title="Remove Step">
            <LargeButton
              type="button"
              disabled={!canRemove}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                removeStep(index)
              }}
              aria-label={`Remove step ${index + 1}`}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </LargeButton>
          </Tooltip>
        </div>
      </td>
    </tr>
  )
}

const AddressFieldType = () => (
  <>
    <option value={''} disabled hidden>
      -Field Type-
    </option>
    <option value={'name'}>Name</option>
    <option value={'email'}>Email</option>
    <option value={'line1'}>Line1</option>
    <option value={'line2'}>Line2</option>
    <option value={'city'}>City</option>
    <option value={'state'}>State</option>
    <option value={'zip'}>Zip</option>
    <option value={'country'}>Country</option>
    <option value={'phone'}>Phone</option>
    <option value={'business'}>Business</option>
  </>
)

const OrderFieldType = () => (
  <>
    <option value={''} disabled hidden>
      -Field Type-
    </option>
    <option value={'id'}>Order ID</option>
    <option value={'created'}>Created</option>
    <option value={'b_to_b'}>B2B</option>
    <option value={'edi'}>EDI</option>
    <option value={'hot'}>Hot</option>
    <option value={'volume'}>Volume (cu ft)</option>
    <option value={'weight'}>Weight (oz)</option>
    <option value={'originator_id'}>Originator ID</option>
    <option value={'originator_number'}>Originator Number</option>
    <option value={'shop_shipping_method_text'}>Shipping Method Text</option>
    <option value={'saturday_delivery'}>Saturday Delivery</option>
    <option value={'gift'}>Gift</option>
    <option value={'tags'}>Tags (comma separated)</option>
    <option value={'sortable_key'}>Order Items Sortable Key</option>
    <option value={'ship_before'}>Ship Before Date</option>
    <option value={'ship_after'}>Ship After Date</option>
    <option value={'order_recurring'}>Recurring Orders</option>
    <option value={'customers_first_order'}>First Customer Order</option>
    <option value={'locally_modified'}>Locally Modified</option>
    <option value={'shipment__transport_mode'}>Transport Mode</option>
    <option value={'is_replacement'}>Replacement Order</option>
  </>
)

const OrderItemFieldType = () => (
  <>
    <option value={''} disabled hidden>
      -Field Type-
    </option>
    <option value={'product_variant__sku'}>SKU</option>
    <option value={'product_variant__barcode'}>Barcode</option>
    <option value={'quantity'}>Quantity</option>
    <option value={'product_variant__product__promo'}>Promo (true/false)</option>
    <option value={'product_variant__is_bundle'}>Bundle (true/false)</option>
    <option value={'product_variant__weight'}>Variant Weight</option>
    <option value={'product_variant__length'}>Variant Length</option>
    <option value={'product_variant__width'}>Variant Width</option>
    <option value={'product_variant__height'}>Variant Height</option>
  </>
)

const OrderOperators = () => (
  <>
    <option value={''} disabled hidden>
      -Field Operator-
    </option>
    <option value={'exact'}>is</option>
    <option value={'exclude_exact'}>is not</option>
    <option value={'istartswith'}>starts with</option>
    <option value={'iendswith'}>ends with</option>
    <option value={'lt'}>less than</option>
    <option value={'lte'}>less than or equal</option>
    <option value={'gt'}>greater than</option>
    <option value={'gte'}>greater than or equal</option>
    <option value={'icontains'}>contains</option>
    <option value={'iregex'}>matches (regular exp)</option>
    <option value={'isnull'}>is blank</option>
    <option value={'exclude_isnull'}>is not blank</option>
    <option value={'in'}>in (list)</option>
  </>
)

type SortFunctionProps = {
  sort: SortProps[]
  setSort: any
}
export const Sort: FunctionComponent<SortFunctionProps> = ({ sort, setSort }) => {
  function addRow() {
    const id = 'added' + sort.length
    setSort((sort: SortProps[]) => sort.concat({ ...defaultSort, id: id }))
  }

  function handleChange(value: string, type: string, c: any, index: number) {
    setSort((sort: SortProps[]) => [...sort.slice(0, index), { ...c, [type]: value }, ...sort.slice(index + 1)])
  }

  return (
    <GlobalStyles.DataTable>
      <tbody>
        {sort.map((c, index) => (
          <GlobalStyles.TableRow key={index}>
            <TableCell center input={getOrdinal(index + 1)} />
            <TableCell>
              <Form.Select
                required
                value={c.field_type}
                onChange={(e) => handleChange(e.target.value, 'field_type', c, index)}
              >
                <option value={''} disabled hidden>
                  -Field-
                </option>
                <option value={'order_field'}>Order Field</option>
                <option value={'order_item'}>Order Items</option>
              </Form.Select>
            </TableCell>
            {c.field_type === 'order_field' ? (
              <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                <Form.Select
                  required
                  value={c.field_value}
                  onChange={(e) => handleChange(e.target.value, 'field_value', c, index)}
                >
                  <OrderFieldType />
                </Form.Select>
              </TableCell>
            ) : c.field_type === 'address_field' ? (
              <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                <Form.Select
                  required
                  value={c.field_value}
                  onChange={(e) => handleChange(e.target.value, 'field_value', c, index)}
                >
                  <AddressFieldType />
                </Form.Select>
              </TableCell>
            ) : c.field_type === 'order_item' ? (
              <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                <Form.Select
                  required
                  value={c.field_value}
                  onChange={(e) => handleChange(e.target.value, 'field_value', c, index)}
                >
                  <OrderItemFieldType />
                </Form.Select>
              </TableCell>
            ) : null}
            <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
              <Form.Select required value={c.sort} onChange={(e) => handleChange(e.target.value, 'sort', c, index)}>
                <option value={'ascending'}>Ascending</option>
                <option value={'descending'}>Descending</option>
              </Form.Select>
            </TableCell>
            <TableCell
              style={{ width: 75 }}
              input={
                <div className="dropdown__container">
                  {sort.length > 1 ? (
                    <button onClick={() => setSort((sort: SortProps[]) => sort.filter((e: any) => e !== c))}>
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  ) : null}
                  <button onClick={() => addRow()}>
                    <FontAwesomeIcon icon={faLayerPlus} />
                  </button>
                </div>
              }
              center
            />
          </GlobalStyles.TableRow>
        ))}
      </tbody>
    </GlobalStyles.DataTable>
  )
}

type LogicProps = {
  filters: FiltersProps
  setFilters: (filters: FiltersProps) => void
  children?: React.ReactNode
  r: number
  g: number
  b: number
}
const Logic: FunctionComponent<LogicProps> = ({ filters, setFilters, children, r, g, b }) => {
  const [logical, setLogical] = useState(filters.logical)
  useEffect(() => {
    setFilters({ ...filters, logical: logical })
  }, [logical])

  function addRow() {
    setLogical((logical: FiltersProps[]) => [...logical, defaultFilters])
  }

  const setType = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'logical_not') {
      setLogical([defaultFilters])
    } else {
      setLogical([defaultFilters, defaultFilters])
    }
    setFilters({ ...defaultFilters, type: e.target.value })
  }

  const isLogical = ['logical_and', 'logical_or', 'logical_not'].includes(filters.type)
  const isOrderItem = ['order_item_any', 'order_item_all', 'order_item_none'].includes(filters.type)

  return (
    <GlobalStyles.DataTable>
      <tbody>
        <tr style={{ border: 'unset' }}>
          <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
            <Form.Select required value={filters.type} onChange={(e) => setType(e)}>
              <option value={''} disabled hidden>
                -Field-
              </option>
              <option value={'order_field'}>Order Field</option>
              <option value={'address_field'}>Address Field</option>
              <optgroup label="Logical Type">
                <option value={'logical_and'}>AND</option>
                <option value={'logical_or'}>OR</option>
                <option value={'logical_not'}>NOT</option>
              </optgroup>
              <optgroup label="Order Item Options">
                <option value={'order_item_any'}>Match ANY Order Items</option>
                <option value={'order_item_all'}>Match ALL Order Items</option>
                <option value={'order_item_none'}>Match No Order Items</option>
              </optgroup>
            </Form.Select>
            {isLogical ? (
              <GlobalStyles.DataTable>
                <tbody>
                  <tr style={{ border: 'unset' }}>
                    <TableCell style={{ background: `rgb(${r}, ${g}, ${b})`, border: '1px solid #e1eaf1' }}>
                      {logical.map((newFilters: FiltersProps, index: number) => (
                        <React.Fragment key={index}>
                          <Logic
                            filters={newFilters}
                            setFilters={(e: any) =>
                              setLogical((logical: FiltersProps[]) => [
                                ...logical.slice(0, index),
                                { ...e },
                                ...logical.slice(index + 1),
                              ])
                            }
                            r={r * 0.95}
                            g={g * 0.95}
                            b={b * 0.95}
                          >
                            {filters.type !== 'logical_not' ? (
                              <TableCell
                                center
                                style={{ verticalAlign: 'unset', width: 50, background: 'rgb(0,0,0,0)' }}
                                input={
                                  <div className="dropdown__container" style={{ minWidth: 60 }}>
                                    {logical.length > 2 ? (
                                      <button
                                        onClick={() =>
                                          setLogical((logical: FiltersProps[]) =>
                                            logical.filter((c) => c !== newFilters)
                                          )
                                        }
                                      >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                      </button>
                                    ) : null}
                                    <button onClick={() => addRow()}>
                                      <FontAwesomeIcon icon={faLayerPlus} />
                                    </button>
                                  </div>
                                }
                              />
                            ) : null}
                          </Logic>
                          {index + 1 !== logical.length ? (
                            <StatusBoxStyled
                              className={'blue'}
                              style={{ margin: '1em auto', width: 100, textAlign: 'center' }}
                            >
                              {filters.type === 'logical_and' ? 'AND' : 'OR'}
                            </StatusBoxStyled>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </TableCell>
                  </tr>
                </tbody>
              </GlobalStyles.DataTable>
            ) : null}
          </TableCell>
          {filters.type === 'order_field' ? (
            <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
              <Form.Select
                required
                value={filters.result.order_field_type}
                onChange={(e) =>
                  setFilters({ ...filters, result: { ...filters.result, order_field_type: e.target.value } })
                }
              >
                <OrderFieldType />
              </Form.Select>
            </TableCell>
          ) : filters.type === 'address_field' ? (
            <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
              <Form.Select
                required
                value={filters.result.order_field_type}
                onChange={(e) =>
                  setFilters({ ...filters, result: { ...filters.result, order_field_type: e.target.value } })
                }
              >
                <AddressFieldType />
              </Form.Select>
            </TableCell>
          ) : isOrderItem ? (
            <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
              <Form.Select
                required
                value={filters.result.order_field_type}
                onChange={(e) =>
                  setFilters({ ...filters, result: { ...filters.result, order_field_type: e.target.value } })
                }
              >
                <OrderItemFieldType />
              </Form.Select>
            </TableCell>
          ) : null}
          {!isLogical ? (
            <>
              <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                <Form.Select
                  required
                  value={filters.result.order_field_operator}
                  onChange={(e) =>
                    setFilters({
                      ...filters,
                      result: { ...filters.result, order_field_operator: e.target.value },
                    })
                  }
                >
                  <OrderOperators />
                </Form.Select>
              </TableCell>
              {!['is_blank', 'is_not_blank'].includes(filters.result.order_field_operator) ? (
                <TableCell style={{ background: 'rgb(0,0,0,0)' }}>
                  <Form.Control
                    type="text"
                    required
                    placeholder=""
                    value={filters.result.order_field_value}
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        result: { ...filters.result, order_field_value: e.target.value },
                      })
                    }
                  />
                </TableCell>
              ) : null}
            </>
          ) : null}
          {children}
        </tr>
      </tbody>
    </GlobalStyles.DataTable>
  )
}

const MultiSelect = styled.div`
  .basic-multi-select > div {
    min-height: 40px;
  }
`

const companyToOption = (company: any) => ({
  label: `(${company.gradingStatus?.split('')[0] || '-'}) ${company.name}`,
  value: company.id,
})

const loadCompanyOptions = async (inputValue: string) => {
  const query = inputValue.trim()
  const response = await fetch(query ? `/api/company/?q=${encodeURIComponent(query)}` : '/api/company/')
  if (!response.ok) {
    return []
  }
  const companies = await response.json()
  return (Array.isArray(companies) ? companies : companies?.results || [])
    .sort((a: { grading: number }, b: { grading: number }) => a.grading - b.grading)
    .map(companyToOption)
}

const ReservationRuleGroup = styled.div`
  margin-top: 1.5em;
  padding-left: 0;

  &:first-of-type {
    margin-top: 0;
  }

  .rule-group-label {
    margin: 0 0 0.75em;
    padding: 0.6em 0.8em;
    border-radius: 4px;
    background: #f6f9ff;
    color: #495057;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .rule-group-content {
    padding-left: 0;
  }

  .rule-group-content > .row {
    --bs-gutter-x: 1rem;
    --bs-gutter-y: 0.75rem;
    margin-right: 0;
    margin-left: 0;
  }

  .eligibility-panel {
    height: 100%;
    padding: 1em;
    border: 1px solid #ebedf2;
    border-radius: 6px;
    background: #ffffff;
  }

  .eligibility-panel-title {
    margin: 0 0 0.75em;
    color: #495057;
    font-size: 0.9rem;
    font-weight: 600;
  }

  .source-ordering-number {
    justify-content: center;
    width: 2.75em;
  }

  .zone-rule-label {
    width: 6em;
  }

  .ordering-row {
    display: flex;
    gap: 0.75em;
    align-items: center;
    margin-bottom: 0.75em;
    padding: 0.75em;
    border: 1px solid #ebedf2;
    border-radius: 6px;
    background: #ffffff;
  }

  .ordering-row:hover {
    border-color: #dce3ff;
    background: #fbfcff;
  }

  .ordering-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25em;
    height: 2.25em;
    border: 0;
    border-radius: 4px;
    background: #f6f9ff;
    color: #74788d;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .ordering-index {
    display: inline-flex;
    flex: 0 0 2.5em;
    align-items: center;
    justify-content: center;
    height: 2.5em;
    border-radius: 50%;
    background: #f6f9ff;
    color: #495057;
    font-weight: 700;
  }

  .ordering-copy {
    flex: 1;

    strong {
      display: block;
    }

    span {
      color: #74788d;
      font-size: 0.85rem;
    }
  }
`

const ReservationStepLayout = styled.div`
  display: block;

  .step-list {
    margin-bottom: 1.25em;
  }

  .step-table-wrap {
    margin-bottom: 1em;
    border: 1px solid #dfe3eb;
    border-radius: 8px;
    overflow: hidden;
  }

  .step-table {
    width: 100%;
    border-collapse: collapse;
    background: #ffffff;
  }

  .step-list-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75em;
    margin-bottom: 0.75em;
    padding: 1em;
    border: 1px solid #ebedf2;
    border-radius: 8px;
    background: #fbfcff;

    h5 {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 700;
    }

    span {
      color: #74788d;
      font-size: 0.8rem;
    }
  }

  .step-table th,
  .step-table td {
    padding: 0.65em 0.75em;
    border-bottom: 1px solid #ebedf2;
    color: #495057;
    text-align: left;
    vertical-align: middle;
  }

  .step-table th {
    background: #fbfcff;
    color: #74788d;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .step-table tr:last-child td {
    border-bottom: 0;
  }

  .step-row {
    cursor: pointer;
  }

  .step-drag-cell,
  .step-action-cell {
    width: 1%;
    text-align: center !important;
    white-space: nowrap;
  }

  .step-drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25em;
    height: 2.25em;
    border: 0;
    border-radius: 4px;
    background: #f6f9ff;
    color: #74788d;
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  .step-row:hover:not(.active) {
    background: #f6f9ff;
  }

  .step-row.active {
    background: #eef4ff;
    box-shadow: inset 4px 0 0 #3d5a98;
  }

  .step-row.active td {
    border-bottom-color: #d8e4ff;
  }

  .step-row.active .step-table-number {
    background: #3d5a98;
    color: #ffffff;
  }

  .step-table-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2em;
    height: 2em;
    border-radius: 50%;
    background: #eaf0ff;
    color: #3d5a98;
    font-weight: 700;
  }

  .step-table-name-input {
    min-width: 220px;
  }

  .step-method-cell span {
    display: block;

    & + span {
      margin-top: 0.2em;
      color: #74788d;
      font-size: 0.85rem;
    }
  }

  .step-row-actions {
    display: flex;
    gap: 6px;
    justify-content: center;
    width: 102px;
    margin: 0 auto;
  }

  .step-add-row td {
    padding: 0;
    border-bottom: 0;
  }

  .step-add-button {
    width: 100%;
    padding: 0.75em 0.9em;
    border: 0;
    background: #ffffff;
    color: #3d5a98;
    font-weight: 700;
    text-align: left;
  }

  .step-add-button:hover {
    background: #f6f9ff;
  }

  @media (max-width: 767px) {
    .step-table-wrap {
      overflow-x: auto;
    }

    .step-table {
      min-width: 560px;
    }
  }

  .full-width {
    width: 100%;
  }

  .step-editor {
    min-width: 0;
    padding: 1em;
    border: 1px solid #dfe3eb;
    border-radius: 8px;
    background: #ffffff;
  }
`

const WaveFormSection = styled.div`
  padding: 1.5em 1em 1em;

  &.separated {
    border-top: 1px solid #ebedf2;
  }

  .section-content {
    padding-left: 0;
  }

  h3 {
    margin-bottom: 0.85em;
    color: #495057;
    font-size: 1.05rem;
    font-weight: 700;
  }

  .section-surface {
    padding: 1em;
    border: 1px solid #dfe3eb;
    border-radius: 8px;
    background: #ffffff;
  }

  .setting-card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.85em;
  }

  .setting-card {
    padding: 1em;
    border: 1px solid #ebedf2;
    border-radius: 6px;
    background: #fbfcff;
  }

  .setting-card .rule-group-label {
    margin: 0 0 0.75em;
    padding: 0.6em 0.8em;
    border-radius: 4px;
    background: #f6f9ff;
    color: #495057;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .setting-card.wide {
    margin-bottom: 0.85em;
  }

  .toggle-card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.85em;
    margin-top: 0.85em;
  }

  .toggle-card {
    display: flex;
    gap: 0.75em;
    align-items: flex-start;
    padding: 0.85em 1em;
    border: 1px solid #ebedf2;
    border-radius: 6px;
    background: #ffffff;
  }

  .toggle-card p,
  .toggle-card span {
    margin: 0;
    padding: 0 !important;
    line-height: 24px;
  }
`

const waveTabMapping = [
  { label: 'Schedules', hash: 'schedules', icon: faCalendarDay },
  { label: 'Waves', hash: 'waves', icon: faLayerGroup },
  { label: 'Create Wave', hash: 'create-wave', icon: faLayerPlus },
  { label: 'Run History', hash: 'run-history', icon: faHistory },
]

export const BatchProfile = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [coverageModalOpen, setCoverageModalOpen] = useState(false)
  const [refreshedSchedules, setRefreshedSchedules] = useState<WaveSchedule[] | null>(null)
  const {
    response: profileResponse,
    loaded: profilesLoaded,
    error: profilesError,
  }: any = useFetch('/api/inventory/wave-profile/', {})
  const scheduleQuery = useMemo(() => {
    const today = new Date()
    const weekStartsOn = { weekStartsOn: 1 as const }
    return buildWaveScheduleListUrl(
      format(startOfWeek(startOfMonth(today), weekStartsOn), 'yyyy-MM-dd'),
      format(endOfWeek(endOfMonth(today), weekStartsOn), 'yyyy-MM-dd')
    )
  }, [])
  const {
    response: scheduleResponse,
    loaded: schedulesLoaded,
    error: schedulesError,
  }: any = useFetch(scheduleQuery, {})
  useEffect(() => {
    setRefreshedSchedules(null)
  }, [scheduleResponse])
  const tabFromUrl = new URLSearchParams(location.search).get('tab')
  const normalizedTabFromUrl =
    tabFromUrl === 'run-waves' ? 'waves' : tabFromUrl === 'create-waves' ? 'create-wave' : tabFromUrl
  const activeTab = waveTabMapping.find((tab) => tab.hash === normalizedTabFromUrl)?.label || waveTabMapping[0].label

  const setWaveTab = (nextTab: string) => {
    const params = new URLSearchParams(location.search)
    const nextHash = waveTabMapping.find((tab) => tab.label === nextTab)?.hash
    if (!nextHash) return
    params.set('tab', nextHash)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true })
  }

  return (
    <>
      <WaveOperationsSummary
        profileResponse={profileResponse}
        profilesLoaded={profilesLoaded}
        scheduleResponse={refreshedSchedules ?? scheduleResponse}
        schedulesLoaded={schedulesLoaded}
        onOpenCoverage={() => setCoverageModalOpen(true)}
      />
      <WaveCoverageModal open={coverageModalOpen} onOpenChange={setCoverageModalOpen} />
      <FullTabMenuCard tabMapping={waveTabMapping} activeTab={activeTab} setActiveTab={setWaveTab}>
        {activeTab === 'Schedules' && (
          <WaveScheduleDashboard
            profileResponse={profileResponse}
            profilesLoaded={profilesLoaded}
            profilesError={profilesError}
            scheduleResponse={refreshedSchedules ?? scheduleResponse}
            schedulesLoaded={schedulesLoaded}
            schedulesError={schedulesError}
            onSchedulesChange={setRefreshedSchedules}
          />
        )}
        {activeTab === 'Waves' && (
          <WaveProfile title="Waves" embedded />
        )}
        {activeTab === 'Create Wave' && <CustomWaveDirective embedded />}
        {activeTab === 'Run History' && <WaveRunHistoryTable />}
      </FullTabMenuCard>
    </>
  )
}

export const CustomWaveDirective = ({ embedded = false }: { embedded?: boolean }) => {
  const [settings, setSettings] = useState({
    companies: [],
    min_orders_batch: 0,
    max_orders_batch: 25,
    min_orders_pre_pack: 20,
    max_orders_pre_pack: 250,
    batch_together: false,
    single_item_batch: false,
    remove_reservations: false,
    ignore_gestation: false,
    sort: false,
    filter: false,
    reservation_strategy: defaultReservationStrategy,
    reservation_profile: {
      mode: 'step_sequence',
      steps: [defaultReservationStep],
    },
  })
  const node = useRef<HTMLFormElement | null>(null)
  const [validated, setValidated] = useState(false)
  const [nameModal, setNameModal] = useState(false)
  const [preview, setPreview] = useState(false)
  const [sort, setSort] = useState<SortProps[]>([defaultSort])
  const [filters, setFilters] = useState<FiltersProps>(defaultFilters)

  const {
    state: { csrf },
  } = globalState()

  const checkValidity = () => {
    setValidated(true)
    if (node?.current?.checkValidity() === false) {
      return false
    } else if (!settings.companies.length) {
      notify({ type: 'error', message: 'Choose at least one company first.' })
      return false
    }
    return true
  }

  const handleSubmit = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
    if (!checkValidity()) {
      return
    }
    usePost(
      '/api/inventory/wave-directive/custom_directive/',
      { ...{ settings, sort, filters } },
      csrf,
      false,
      true,
      true
    ).then((response) => {
      if (response?.error) {
        notify({ type: 'warning', message: response?.error })
      } else {
        notify({ type: 'success', message: 'Batched Successfully' })
      }
    })
  }

  const handlePreview = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
    if (!checkValidity()) {
      return
    }

    setPreview(true)
    setValidated(false)
  }

  const content = (
    <>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>Wave Directive Builder</h3>
        </GlobalStyles.CardTitle>
      </GlobalStyles.CardHeader>
      <Form ref={node} validated={validated} onSubmit={handleSubmit} style={{ padding: '0 1em 2em' }}>
        <WaveDirectiveSettings {...{ settings, setSettings, sort, setSort, filters, setFilters }} showCompanies>
          <GlobalStyles.Button
            type="button"
            className="royal"
            style={{ minWidth: 125, float: 'right' }}
            onClick={() => checkValidity() && setNameModal(true)}
          >
            Save Directive
          </GlobalStyles.Button>
          <GlobalStyles.Button style={{ minWidth: 125, float: 'right' }} onClick={handleSubmit}>
            Run Once
          </GlobalStyles.Button>
          <GlobalStyles.Button className="secondary" style={{ minWidth: 125, float: 'right' }} onClick={handlePreview}>
            Preview
          </GlobalStyles.Button>
        </WaveDirectiveSettings>
      </Form>
      <WavePreview {...{ settings, sort, filters }} open={preview} setOpen={setPreview} />
      <WaveCreate {...{ settings, sort, filters }} open={nameModal} setOpen={setNameModal} />
    </>
  )

  return embedded ? content : <GlobalStyles.FullPageCard>{content}</GlobalStyles.FullPageCard>
}

type WaveDirectiveSettingsProps = {
  settings: any
  setSettings: (settings: any) => void
  sort: any
  setSort: (sort: SortProps[]) => void
  filters: any
  setFilters: (filters: FiltersProps) => void
  showCompanies?: boolean
  children?: React.ReactNode
}

export const WaveDirectiveSettings: FunctionComponent<WaveDirectiveSettingsProps> = ({
  settings,
  setSettings,
  sort,
  setSort,
  filters,
  setFilters,
  showCompanies,
  children,
}) => {
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))
  const baseReservationStrategy = { ...defaultReservationStrategy, ...(settings.reservation_strategy || {}) }
  const reservationSteps = normalizeReservationSteps(settings, baseReservationStrategy)
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const safeActiveStepIndex = Math.min(activeStepIndex, reservationSteps.length - 1)
  const reservationStrategy = reservationSteps[safeActiveStepIndex]

  const setReservationSteps = (steps: ReservationStep[], nextActiveStepIndex = safeActiveStepIndex) => {
    const normalizedSteps = steps.map(normalizeReservationStep)
    const firstStep = normalizedSteps[0]
    setActiveStepIndex(Math.min(nextActiveStepIndex, normalizedSteps.length - 1))
    setSettings({
      ...settings,
      reservation_strategy: {
        inventory_sources: firstStep.inventory_sources,
        controlled_date_ordering: firstStep.controlled_date_ordering,
        pick_source_ordering: firstStep.pick_source_ordering,
        lot_mixing: firstStep.lot_mixing,
        container_ordering: firstStep.container_ordering,
        ordering_order: firstStep.ordering_order,
        location_attributes: firstStep.location_attributes,
      },
      reservation_profile: {
        mode: 'step_sequence',
        steps: normalizedSteps,
      },
    })
  }

  const setReservationStrategy = (updates: Partial<ReservationStep>) => {
    setReservationSteps(
      reservationSteps.map((step, index) => (index === safeActiveStepIndex ? { ...step, ...updates } : step))
    )
  }

  const updateReservationStep = (stepIndex: number, updates: Partial<ReservationStep>) => {
    setReservationSteps(
      reservationSteps.map((step, index) => (index === stepIndex ? { ...step, ...updates } : step)),
      stepIndex
    )
  }

  const inventorySources = normalizeInventorySources(reservationStrategy)
  const normalizedZonePolicy = {
    include: normalizeZoneValues(reservationStrategy.location_attributes?.zone?.include),
    exclude: normalizeZoneValues(reservationStrategy.location_attributes?.zone?.exclude),
    ordering: normalizeZoneValues(reservationStrategy.location_attributes?.zone?.ordering),
  }
  const orderingOrder = normalizeOrderingOrder(reservationStrategy.ordering_order)
  const setInventorySources = (updates: Partial<typeof defaultReservationStrategy.inventory_sources>) => {
    setReservationStrategy({
      inventory_sources: { ...inventorySources, ...updates },
    })
  }

  const toggleAllowedInventorySource = (source: string) => {
    const nextAllowed = inventorySources.allowed.includes(source)
      ? inventorySources.allowed.filter((allowedSource: string) => allowedSource !== source)
      : inventorySources.allowed.concat(source)
    const safeAllowed = nextAllowed.length ? nextAllowed : ['pickable']
    const nextOrdering = inventorySources.ordering.filter((orderingSource: string) =>
      safeAllowed.includes(orderingSource)
    )

    setInventorySources({ allowed: safeAllowed, ordering: nextOrdering.length ? nextOrdering : [safeAllowed[0]] })
  }

  const updateInventorySourceOrdering = (index: number, value: string) => {
    setInventorySources({
      ordering: inventorySources.ordering.map((source: string, sourceIndex: number) =>
        sourceIndex === index ? value : source
      ),
    })
  }

  const removeInventorySourceOrdering = (index: number) => {
    const nextOrdering = inventorySources.ordering.filter((_: string, sourceIndex: number) => sourceIndex !== index)
    setInventorySources({ ordering: nextOrdering.length ? nextOrdering : [inventorySources.allowed[0]] })
  }

  const addInventorySource = () => {
    const nextSource = inventorySources.allowed.find((source: string) => !inventorySources.ordering.includes(source))
    if (nextSource) {
      setInventorySources({ ordering: inventorySources.ordering.concat(nextSource) })
    }
  }

  const setZoneEligibility = (updates: Partial<LocationAttributePolicy>) => {
    const nextInclude = normalizeZoneValues(updates.include ?? normalizedZonePolicy.include)
    const nextExclude = normalizeZoneValues(updates.exclude ?? normalizedZonePolicy.exclude)
    const nextOrdering = normalizedZonePolicy.ordering.filter(
      (zone) => !nextExclude.includes(zone) && (!nextInclude.length || nextInclude.includes(zone))
    )

    setReservationStrategy({
      location_attributes: {
        ...defaultLocationAttributes,
        ...reservationStrategy.location_attributes,
        zone: {
          ...defaultLocationAttributes.zone,
          ...reservationStrategy.location_attributes.zone,
          include: nextInclude,
          exclude: nextExclude,
          ordering: nextOrdering,
        },
      },
    })
  }

  const setZoneOrdering = (values: string[]) => {
    setLocationAttributePolicy('zone', 'ordering', values)
  }

  const setLocationAttributePolicy = (
    field: keyof LocationAttributes,
    policyKey: keyof LocationAttributePolicy,
    values: string[]
  ) => {
    setReservationStrategy({
      location_attributes: {
        ...defaultLocationAttributes,
        ...reservationStrategy.location_attributes,
        [field]: {
          ...defaultLocationAttributes[field],
          ...reservationStrategy.location_attributes[field],
          [policyKey]: values,
        },
      },
    })
  }

  const handleOrderingDragEnd = ({ active, over }: any) => {
    if (!over || active.id === over.id) return

    const oldIndex = orderingOrder.indexOf(active.id)
    const newIndex = orderingOrder.indexOf(over.id)
    setReservationStrategy({ ordering_order: arrayMove(orderingOrder, oldIndex, newIndex) })
  }

  const handleStepDragEnd = ({ active, over }: any) => {
    if (!over || active.id === over.id) return

    const oldIndex = reservationSteps.findIndex((step) => step.id === active.id)
    const newIndex = reservationSteps.findIndex((step) => step.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return
    setReservationSteps(arrayMove(reservationSteps, oldIndex, newIndex), newIndex)
  }

  const addReservationStep = () => {
    const nextStep = normalizeReservationStep(
      {
        ...defaultReservationStep,
        id: `step-${Date.now()}`,
        name: `Fallback step ${reservationSteps.length}`,
        inventory_sources: { allowed: ['backstock'], ordering: ['backstock'] },
        container_ordering: 'full_container_first',
      },
      reservationSteps.length
    )
    setReservationSteps(reservationSteps.concat(nextStep), reservationSteps.length)
  }

  const duplicateReservationStepAt = (stepIndex: number) => {
    const sourceStep = reservationSteps[stepIndex]
    if (!sourceStep) return

    const nextStep = normalizeReservationStep(
      {
        ...sourceStep,
        id: `step-${Date.now()}`,
        name: `${sourceStep.name} copy`,
      },
      stepIndex + 1
    )
    setReservationSteps(
      reservationSteps.slice(0, stepIndex + 1).concat(nextStep, reservationSteps.slice(stepIndex + 1)),
      stepIndex + 1
    )
  }

  const removeReservationStepAt = (stepIndex: number) => {
    if (reservationSteps.length === 1) return
    setReservationSteps(
      reservationSteps.filter((_, index) => index !== stepIndex),
      Math.max(Math.min(stepIndex, reservationSteps.length - 2), 0)
    )
  }

  return (
    <>
      <WaveFormSection>
        <h3>Batch Settings</h3>
        <div className="section-content section-surface">
          {showCompanies && (
            <div className="setting-card wide">
              <Form.Group className="required">
                <Form.Label>Companies</Form.Label>
                <MultiSelect>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadCompanyOptions}
                    placeholder="Search companies..."
                    value={settings.companies}
                    onChange={(e: any) => setSettings({ ...settings, companies: e })}
                    closeMenuOnSelect={false}
                    isMulti
                    className="basic-multi-select"
                    required
                  />
                </MultiSelect>
              </Form.Group>
            </div>
          )}
          <div className="setting-card-grid">
            <div className="setting-card">
              <h5 className="rule-group-label">Batch Size Limits</h5>
              <Row>
                <Form.Group as={Col} md="6">
                  <Form.Label>Min orders per batch</Form.Label>
                  <Form.Control
                    type="number"
                    name="min_orders_batch"
                    placeholder="Min # Batch Orders"
                    value={settings.min_orders_batch}
                    onChange={(e) => setSettings({ ...settings, min_orders_batch: e.target.value })}
                  />
                </Form.Group>
                <Form.Group as={Col} md="6">
                  <Form.Label>Max orders per batch</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_orders_batch"
                    placeholder="Max # Batch Orders"
                    value={settings.max_orders_batch}
                    onChange={(e) => setSettings({ ...settings, max_orders_batch: e.target.value })}
                  />
                </Form.Group>
              </Row>
            </div>
            <div className="setting-card">
              <h5 className="rule-group-label">Pre Pack Limits</h5>
              <Row>
                <Form.Group as={Col} md="6">
                  <Form.Label>Min orders per Pre Pack</Form.Label>
                  <Form.Control
                    type="number"
                    name="min_orders_pre_pack"
                    placeholder="Min # PrePack Orders"
                    value={settings.min_orders_pre_pack}
                    onChange={(e) => setSettings({ ...settings, min_orders_pre_pack: e.target.value })}
                  />
                </Form.Group>
                <Form.Group as={Col} md="6">
                  <Form.Label>Max orders per Pre Pack</Form.Label>
                  <Form.Control
                    type="number"
                    name="max_orders_pre_pack"
                    placeholder="Max # PrePack Orders"
                    value={settings.max_orders_pre_pack}
                    onChange={(e) => setSettings({ ...settings, max_orders_pre_pack: e.target.value })}
                  />
                </Form.Group>
              </Row>
            </div>
          </div>
          <div className="toggle-card-grid">
            {settings.companies.length > 1 && (
              <div className="toggle-card">
                <Checkbox
                  selected={settings.batch_together}
                  setSelected={() => setSettings({ ...settings, batch_together: !settings.batch_together })}
                />
                <span>
                  <HoverNotes
                    description="Batching together means orders from multiple companies will be combined."
                    title="Batch Orders Together"
                  />
                </span>
              </div>
            )}
            <div className="toggle-card">
              <Checkbox
                selected={settings.single_item_batch}
                setSelected={() => setSettings({ ...settings, single_item_batch: !settings.single_item_batch })}
              />
              <p>
                <HoverNotes
                  description="Filters for only single orders and uses single item batching workflow."
                  title="Single Item Batches"
                />
              </p>
            </div>
            <div className="toggle-card">
              <Checkbox
                selected={settings.remove_reservations}
                setSelected={() => setSettings({ ...settings, remove_reservations: !settings.remove_reservations })}
              />
              <p>
                <HoverNotes description="Applies to any existing orders not yet batched." title="Remove Reservations" />
              </p>
            </div>
            <div className="toggle-card">
              <Checkbox
                selected={settings.ignore_gestation}
                setSelected={() => setSettings({ ...settings, ignore_gestation: !settings.ignore_gestation })}
              />
              <p>Ignore Gestation</p>
            </div>
          </div>
        </div>
      </WaveFormSection>
      <WaveFormSection className="separated">
        <h3>Wave Reservation Profile</h3>
        <div className="section-content">
          <div style={{ marginBottom: 12, color: '#74788d', fontSize: '0.9rem' }}>
            Build ordered reservation steps. Each fallback step runs only after earlier steps leave demand unreserved.
          </div>
          <ReservationStepLayout>
            <div className="step-list">
              <div className="step-list-header">
                <h5>Reservation Steps</h5>
                <span>{reservationSteps.length} configured</span>
              </div>
              <div className="step-table-wrap">
                <table className="step-table">
                  <thead>
                    <tr>
                      <th className="step-drag-cell" />
                      <th style={{ width: 64 }}>Step</th>
                      <th>Name</th>
                      <th>Inventory Types</th>
                      <th>Location Scope</th>
                      <th>Selection Method</th>
                      <th className="step-action-cell">Actions</th>
                    </tr>
                  </thead>
                  <DndContext sensors={sensors} onDragEnd={handleStepDragEnd}>
                    <SortableContext
                      items={reservationSteps.map(({ id }) => id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <tbody>
                        {reservationSteps.map((step, index) => (
                          <SortableReservationStepRow
                            key={step.id}
                            step={step}
                            index={index}
                            active={index === safeActiveStepIndex}
                            canRemove={reservationSteps.length > 1}
                            setActiveStepIndex={setActiveStepIndex}
                            updateStepName={(stepIndex, name) => updateReservationStep(stepIndex, { name })}
                            duplicateStep={duplicateReservationStepAt}
                            removeStep={removeReservationStepAt}
                          />
                        ))}
                        <tr className="step-add-row">
                          <td colSpan={7}>
                            <button type="button" className="step-add-button" onClick={addReservationStep}>
                              + Add reservation step
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </SortableContext>
                  </DndContext>
                </table>
              </div>
              <div className="step-editor">
                <ReservationRuleGroup>
                  <h5 className="rule-group-label">Inventory Eligibility</h5>
                  <div className="rule-group-content">
                    <Row>
                      <Col md="4" style={{ marginBottom: 12 }}>
                        <div className="eligibility-panel">
                          <h6 className="eligibility-panel-title">Allowed Inventory Types</h6>
                          {inventoryTypeOptions.map(({ value, label }) => (
                            <InputGroup key={value} style={{ marginBottom: 8 }}>
                              <Checkbox
                                selected={inventorySources.allowed.includes(value)}
                                setSelected={() => toggleAllowedInventorySource(value)}
                              />
                              <p style={{ padding: '0 1em', lineHeight: '24px', marginBottom: 0 }}>{label}</p>
                            </InputGroup>
                          ))}
                        </div>
                      </Col>
                      <Col md="4" style={{ marginBottom: 12 }}>
                        <div className="eligibility-panel">
                          <h6 className="eligibility-panel-title">Inventory Type Ordering</h6>
                          {inventorySources.ordering.map((source: string, index: number) => (
                            <InputGroup key={`${source}-${index}`} style={{ marginBottom: 8 }}>
                              <InputGroup.Text className="source-ordering-number">{index + 1}</InputGroup.Text>
                              <Form.Select
                                value={source}
                                onChange={(e) => updateInventorySourceOrdering(index, e.target.value)}
                              >
                                {inventoryTypeOptions
                                  .filter(
                                    ({ value }) =>
                                      inventorySources.allowed.includes(value) &&
                                      (value === source || !inventorySources.ordering.includes(value))
                                  )
                                  .map(({ value, label }) => (
                                    <option key={value} value={value}>
                                      {label}
                                    </option>
                                  ))}
                              </Form.Select>
                              <GlobalStyles.Button
                                type="button"
                                className="secondary"
                                style={{ minWidth: 90 }}
                                onClick={() => removeInventorySourceOrdering(index)}
                              >
                                Remove
                              </GlobalStyles.Button>
                            </InputGroup>
                          ))}
                          <InputGroup>
                            <div style={{ flex: 1 }} />
                            <GlobalStyles.Button
                              type="button"
                              className="secondary"
                              style={{ minWidth: 90 }}
                              onClick={addInventorySource}
                              disabled={
                                !inventorySources.allowed.some(
                                  (source: string) => !inventorySources.ordering.includes(source)
                                )
                              }
                            >
                              Add
                            </GlobalStyles.Button>
                          </InputGroup>
                        </div>
                      </Col>
                      <Col md="4" style={{ marginBottom: 12 }}>
                        <div className="eligibility-panel">
                          <h6 className="eligibility-panel-title">Controlled Date</h6>
                          <Form.Group style={{ marginBottom: 12 }}>
                            <Form.Label>Strategy</Form.Label>
                            <Form.Select
                              value={reservationStrategy.controlled_date_ordering}
                              onChange={(e) => setReservationStrategy({ controlled_date_ordering: e.target.value })}
                            >
                              <option value="product_default">Use Product Inventory Control</option>
                              <option value="fefo">Oldest Controlled Date First</option>
                              <option value="newest_expiry_last">Newest Controlled Date First</option>
                              <option value="fifo">Oldest Received First</option>
                              <option value="lifo">Newest Received First</option>
                            </Form.Select>
                            <Form.Text>
                              Uses the product or variant inventory-control date, such as expiration, best-before,
                              manufacture, or received date.
                            </Form.Text>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>Minimum Days Remaining</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              value={reservationStrategy.minimum_days_remaining || ''}
                              placeholder="Example: 180"
                              onChange={(e) => setReservationStrategy({ minimum_days_remaining: e.target.value })}
                            />
                          </Form.Group>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4" style={{ marginBottom: 12 }}>
                        <div className="eligibility-panel">
                          <h6 className="eligibility-panel-title">Lot Mixing</h6>
                          <Form.Select
                            value={reservationStrategy.lot_mixing}
                            onChange={(e) => setReservationStrategy({ lot_mixing: e.target.value })}
                          >
                            <option value="allow">Allow Splits</option>
                            <option value="prefer_single_lot">Prefer Single Lot</option>
                            <option value="require_single_lot">Require Single Lot</option>
                          </Form.Select>
                        </div>
                      </Col>
                      <Col md="4" style={{ marginBottom: 12 }}>
                        <div className="eligibility-panel">
                          <h6 className="eligibility-panel-title">Container Ordering</h6>
                          <Form.Select
                            value={reservationStrategy.container_ordering}
                            onChange={(e) => setReservationStrategy({ container_ordering: e.target.value })}
                          >
                            <option value="loose_first">Loose First</option>
                            <option value="full_container_first">Full Container First</option>
                            <option value="loose_or_container">No Preference</option>
                          </Form.Select>
                        </div>
                      </Col>
                      <Col md="4" style={{ marginBottom: 12 }}>
                        <div className="eligibility-panel">
                          <h6 className="eligibility-panel-title">Pick Source Ordering</h6>
                          <Form.Select
                            value={reservationStrategy.pick_source_ordering}
                            onChange={(e) => setReservationStrategy({ pick_source_ordering: e.target.value })}
                          >
                            <option value="product_default">Product Default</option>
                            <option value="deplete_location">Fewest Locations</option>
                            <option value="largest_available_first">Largest Available First</option>
                            <option value="smallest_available_first">Smallest Available First</option>
                            <option value="pick_path">Pick Path</option>
                          </Form.Select>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </ReservationRuleGroup>
                <ReservationRuleGroup>
                  <h5 className="rule-group-label">Location Eligibility</h5>
                  <div className="rule-group-content">
                    <Row>
                      <Col md="4" style={{ marginBottom: 12 }}>
                        <div className="eligibility-panel">
                          <h6 className="eligibility-panel-title">Zone</h6>
                          <InputGroup style={{ marginBottom: 8 }}>
                            <InputGroup.Text className="zone-rule-label">Include</InputGroup.Text>
                            <div style={{ flex: 1 }}>
                              <LocationValueSelect
                                values={normalizedZonePolicy.include}
                                onChange={(values) => setZoneEligibility({ include: values })}
                                placeholder="Type zone values"
                              />
                            </div>
                          </InputGroup>
                          <InputGroup style={{ marginBottom: 8 }}>
                            <InputGroup.Text className="zone-rule-label">Exclude</InputGroup.Text>
                            <div style={{ flex: 1 }}>
                              <LocationValueSelect
                                values={normalizedZonePolicy.exclude}
                                onChange={(values) => setZoneEligibility({ exclude: values })}
                                placeholder="Type zone values"
                              />
                            </div>
                          </InputGroup>
                          <InputGroup>
                            <InputGroup.Text className="zone-rule-label">Ordering</InputGroup.Text>
                            <div style={{ flex: 1 }}>
                              <LocationValueSelect
                                values={normalizedZonePolicy.ordering}
                                onChange={setZoneOrdering}
                                placeholder="Type ordering values"
                              />
                            </div>
                          </InputGroup>
                        </div>
                      </Col>
                      {(['aisle', 'bay', 'level', 'slot'] as (keyof LocationAttributes)[]).map((field) => (
                        <Col md="4" key={field} style={{ marginBottom: 12 }}>
                          <div className="eligibility-panel">
                            <h6 className="eligibility-panel-title">
                              {field.charAt(0).toUpperCase() + field.slice(1)}
                            </h6>
                            {(['include', 'exclude', 'ordering'] as (keyof LocationAttributePolicy)[]).map(
                              (policyKey) => (
                                <InputGroup key={policyKey} style={{ marginBottom: policyKey === 'ordering' ? 0 : 8 }}>
                                  <InputGroup.Text className="zone-rule-label">
                                    {policyKey.charAt(0).toUpperCase() + policyKey.slice(1)}
                                  </InputGroup.Text>
                                  <div style={{ flex: 1 }}>
                                    <LocationValueSelect
                                      values={reservationStrategy.location_attributes?.[field]?.[policyKey] || []}
                                      onChange={(values) => setLocationAttributePolicy(field, policyKey, values)}
                                      placeholder={`Type ${field} values`}
                                    />
                                  </div>
                                </InputGroup>
                              )
                            )}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </ReservationRuleGroup>
                <ReservationRuleGroup>
                  <h5 className="rule-group-label">Advanced Inventory Ordering</h5>
                  <div className="rule-group-content">
                    <div style={{ marginBottom: 12, color: '#74788d', fontSize: '0.9rem' }}>
                      Eligibility rules filter inventory first. Ordering controls which preference wins when eligible
                      inventory matches multiple rules.
                    </div>
                    <DndContext sensors={sensors} onDragEnd={handleOrderingDragEnd}>
                      <SortableContext items={orderingOrder} strategy={verticalListSortingStrategy}>
                        {orderingOrder.map((orderingValue, index) => (
                          <SortableOrderingRule key={orderingValue} id={orderingValue} index={index} />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                </ReservationRuleGroup>
              </div>
            </div>
          </ReservationStepLayout>
        </div>
      </WaveFormSection>
      <WaveFormSection className="separated">
        <h3>Order Selection</h3>
        <div className="section-content section-surface">
          <div className="setting-card wide">
            <div className="toggle-card" style={{ marginBottom: settings.sort ? 12 : 0 }}>
              <Checkbox
                selected={settings.sort}
                setSelected={(e) => setSettings({ ...settings, sort: !settings.sort })}
              />
              <p>Sort By Order Field (Default is ascending by order id)</p>
            </div>
            {settings.sort ? (
              <div>
                <StatusBoxStyled className={'blue'} style={{ textAlign: 'center' }}>
                  Please note: boolean fields sort from False {'->'} True when ascending
                </StatusBoxStyled>
                <Sort {...{ sort, setSort }} />
              </div>
            ) : null}
          </div>
          <div className="setting-card wide">
            <div className="toggle-card" style={{ marginBottom: settings.filter ? 12 : 0 }}>
              <Checkbox
                selected={settings.filter}
                setSelected={() => setSettings({ ...settings, filter: !settings.filter })}
              />
              <p>Filter Using Logic & Order Fields</p>
            </div>
            {settings.filter ? (
              <div>
                <StatusBoxStyled className={'blue'} style={{ margin: '0 0.9em', textAlign: 'center' }}>
                  Please note: datetime fields must be formatted as 'DD/MM/YYYY'
                </StatusBoxStyled>
                <Logic {...{ filters, setFilters }} r={240} g={248} b={255} />
              </div>
            ) : null}
          </div>
        </div>
      </WaveFormSection>
      <div style={{ padding: '1em 0' }}>{children}</div>
    </>
  )
}

type WaveCreateProps = {
  open: boolean
  setOpen: (open: boolean) => void
  settings: any
  sort: SortProps[]
  filters: FiltersProps
}
const WaveCreate: FunctionComponent<WaveCreateProps> = ({ open, setOpen, settings, sort, filters }) => {
  const {
    state: { csrf },
  } = globalState()

  const node = useRef<HTMLFormElement | null>(null)
  const [validated, setValidated] = useState(false)
  const navigate = useNavigate()

  const [name, setName] = useState('')

  const handleSubmit = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault()
    e.stopPropagation()
    setValidated(true)
    if (node?.current?.checkValidity() === false) {
      notify({ type: 'error', message: 'Choose a name for this Wave Profile.' })
      return
    }
    usePost('/api/inventory/wave-directive/', { ...{ settings, sort, name, filters } }, csrf, false, true, true).then(
      (response) => {
        if (response?.error) {
          notify({ type: 'warning', message: response?.error })
        }
      }
    )

    navigate('/batch/')
  }

  return (
    <GenericModal
      heading={'Wave Profile Name'}
      show={open}
      onHide={() => setOpen(false)}
      buttons={
        <>
          <GlobalStyles.Button onClick={() => setOpen(false)}>Close</GlobalStyles.Button>
          <GlobalStyles.Button onClick={handleSubmit}>Save</GlobalStyles.Button>
        </>
      }
    >
      <Form ref={node} validated={validated} onSubmit={handleSubmit} style={{ padding: '2em' }}>
        <Form.Control
          type="text"
          name="name"
          placeholder="Example: 'Under 1lb, excluding B2B'"
          value={name}
          required
          onChange={(e) => setName(e.target.value)}
        />
      </Form>
    </GenericModal>
  )
}
